import { ForwardedRef, Key, LiHTMLAttributes, forwardRef, useId } from "react";

import {
  FormHelperText,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  SvgIconProps,
} from "@mui/material";
import { gray } from "configs/colors";
import { shadows } from "configs/shadows";
import { rem } from "utils";

import { useSelect } from "./Select.hooks";
import { Container, SelectedIconWrapper, StylesSelect } from "./Select.styles";
import { SelectProps, SelectReference } from "./Select.types";
import { CheckIcon, ChevronDownIcon, XIcon } from "../Icon";
import InputLabel from "../InputLabel/InputLabel";
import Typography from "../Typography/Typography";

const IconComponent = (props: SvgIconProps) => (
  <ChevronDownIcon {...props} fontSize="small" />
);

const SelectInner = <V, R>(
  {
    defaultValue,
    displayEmpty = true,
    fullWidth = true,
    helperText,
    label,
    options,
    placeholder,
    value,
    noOptionsText = "No options",
    tooltipText,
    clearable,
    renderOption,
    renderValue,
    onChange,
    ...innerProps
  }: SelectProps<V>,
  ref: ForwardedRef<R>
) => {
  const { selectRef, valueState, onChangeHandler, onClearStateHandler } =
    useSelect({
      ref,
      defaultValue,
      value,
      onChange,
    });
  const formId = useId();

  return (
    <Container id={innerProps.id}>
      {!!label && (
        <InputLabel
          htmlFor={innerProps.id ?? formId}
          required={innerProps.required}
          helperText={tooltipText}
        >
          {label}
        </InputLabel>
      )}
      <StylesSelect
        {...innerProps}
        ref={selectRef}
        size="small"
        variant="outlined"
        fullWidth={fullWidth}
        displayEmpty={displayEmpty}
        endAdornment={
          valueState && clearable ? (
            <XIcon
              cursor="pointer"
              onClick={onClearStateHandler}
              sx={{
                marginRight: "14px",
                color: "#667085",
                fontSize: "24px",
                "& path": {
                  strokeWidth: "1.5",
                },
              }}
            />
          ) : null
        }
        value={valueState ?? null}
        labelId={innerProps.id ?? formId}
        input={
          <OutlinedInput id={innerProps.id ?? formId} value={valueState} />
        }
        IconComponent={IconComponent}
        onChange={
          onChangeHandler as (event: SelectChangeEvent<unknown>) => void
        }
        MenuProps={{
          disableAutoFocusItem: true,
          sx: {
            ".MuiPaper-root": {
              boxShadow: shadows.lg,
            },
          },
          MenuListProps: {
            sx: {
              padding: `${rem(4)} ${rem(6)} !important`,
              marginTop: rem(-4),
              li: {
                justifyContent: "space-between",
                alignItems: "center",
                padding: `${rem(8)} ${rem(6)}`,
                fontSize: rem(14),
                borderRadius: "6px",
                "&:hover": {
                  backgroundColor: gray[50],
                },
                "&.Mui-selected": {
                  backgroundColor: gray[50],
                  "&:hover": { backgroundColor: gray[50] },
                },
              },
              "li + li": {
                marginTop: rem(4),
              },
            },
          },
        }}
        renderValue={
          (renderValue as unknown as SelectProps<unknown>["renderValue"]) ??
          (valueState === null || valueState === undefined
            ? () => placeholder
            : undefined)
        }
      >
        {!!placeholder && (
          <MenuItem disabled value={undefined} sx={{ display: "none" }}>
            {placeholder}
          </MenuItem>
        )}
        {options.length === 0 ? (
          <MenuItem disabled>{noOptionsText}</MenuItem>
        ) : (
          options.map((i) => (
            <MenuItem
              key={i.value as Key | null | undefined}
              value={i.value as LiHTMLAttributes<V>["value"]}
            >
              {typeof renderOption === "function" ? (
                renderOption(i)
              ) : (
                <Typography display="inline-block" type="TextSM">
                  {i.label}
                </Typography>
              )}
              {i.value === valueState ? (
                <SelectedIconWrapper className="selected-icon-wrapper">
                  <CheckIcon color="primary" fontSize="small" />
                </SelectedIconWrapper>
              ) : null}
            </MenuItem>
          ))
        )}
      </StylesSelect>
      {helperText && (
        <FormHelperText error={innerProps.error}>{helperText}</FormHelperText>
      )}
    </Container>
  );
};

const Select = forwardRef(SelectInner) as <V>(
  props: SelectProps<V> & { ref?: ForwardedRef<SelectReference<V>> }
) => ReturnType<typeof SelectInner>;

export default Select;
